// src/types.ts
var DEFAULT_CONFIG = {
  workMins: 25,
  breakMins: 5,
  longBreakMins: 15,
  dailyGoal: 8
};

// src/timer/store.ts
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { v4 as uuidv4 } from "uuid";
function getDeviceId() {
  if (typeof window !== "undefined" && window.localStorage) {
    let deviceId = localStorage.getItem("pinepomo_device_id");
    if (!deviceId) {
      deviceId = uuidv4();
      localStorage.setItem("pinepomo_device_id", deviceId);
    }
    return deviceId;
  }
  return uuidv4();
}
var createTimerStore = () => create()(
  immer((set, get) => ({
    session: null,
    config: DEFAULT_CONFIG,
    remainingSeconds: 0,
    start: (options) => {
      const { config, session } = get();
      if (session?.status === "running") {
        return;
      }
      const durationMins = options?.durationMins ?? config.workMins;
      const now = /* @__PURE__ */ new Date();
      set((state) => {
        state.session = {
          id: uuidv4(),
          eventId: uuidv4(),
          deviceId: getDeviceId(),
          startedAt: now,
          durationMins,
          status: "running",
          taskName: options?.taskName,
          todoistTaskId: options?.todoistTaskId,
          notionTaskId: options?.notionTaskId,
          createdAt: now,
          updatedAt: now
        };
        state.remainingSeconds = durationMins * 60;
      });
    },
    pause: () => {
      set((state) => {
        if (state.session?.status === "running") {
          state.session.status = "paused";
          state.session.updatedAt = /* @__PURE__ */ new Date();
        }
      });
    },
    resume: () => {
      set((state) => {
        if (state.session?.status === "paused") {
          state.session.status = "running";
          state.session.updatedAt = /* @__PURE__ */ new Date();
        }
      });
    },
    cancel: () => {
      set((state) => {
        if (state.session && (state.session.status === "running" || state.session.status === "paused")) {
          state.session.status = "cancelled";
          state.session.endedAt = /* @__PURE__ */ new Date();
          state.session.updatedAt = /* @__PURE__ */ new Date();
        }
      });
    },
    complete: () => {
      set((state) => {
        if (state.session?.status === "running") {
          state.session.status = "completed";
          state.session.endedAt = /* @__PURE__ */ new Date();
          state.session.updatedAt = /* @__PURE__ */ new Date();
          state.remainingSeconds = 0;
        }
      });
    },
    tick: () => {
      const { session, remainingSeconds } = get();
      if (session?.status !== "running") {
        return;
      }
      if (remainingSeconds <= 1) {
        get().complete();
      } else {
        set((state) => {
          state.remainingSeconds -= 1;
        });
      }
    },
    reset: () => {
      set((state) => {
        state.session = null;
        state.remainingSeconds = 0;
      });
    },
    setConfig: (newConfig) => {
      set((state) => {
        state.config = { ...state.config, ...newConfig };
      });
    }
  }))
);

// src/events/emitter.ts
var TimerEventEmitter = class {
  listeners = /* @__PURE__ */ new Map();
  on(type, callback) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, /* @__PURE__ */ new Set());
    }
    this.listeners.get(type).add(callback);
    return () => {
      this.listeners.get(type)?.delete(callback);
    };
  }
  off(type, callback) {
    this.listeners.get(type)?.delete(callback);
  }
  emit(type, session, remainingSeconds) {
    const event = {
      type,
      session,
      remainingSeconds,
      timestamp: /* @__PURE__ */ new Date()
    };
    this.listeners.get(type)?.forEach((callback) => {
      try {
        callback(event);
      } catch (error) {
        console.error(`Error in timer event listener for ${type}:`, error);
      }
    });
  }
  removeAllListeners() {
    this.listeners.clear();
  }
};
var createTimerEmitter = () => new TimerEventEmitter();

// src/storage/port.ts
var MemoryStorageAdapter = class {
  sessions = /* @__PURE__ */ new Map();
  config = null;
  async saveSession(session) {
    this.sessions.set(session.id, session);
  }
  async getSession(id) {
    return this.sessions.get(id) ?? null;
  }
  async getCurrentSession() {
    for (const session of this.sessions.values()) {
      if (session.status === "running" || session.status === "paused") {
        return session;
      }
    }
    return null;
  }
  async getSessionsByDate(date) {
    const dateStr = date.toISOString().split("T")[0];
    return Array.from(this.sessions.values()).filter((s) => {
      const sessionDateStr = s.startedAt.toISOString().split("T")[0];
      return sessionDateStr === dateStr;
    });
  }
  async getAllSessions() {
    return Array.from(this.sessions.values());
  }
  async getSettings() {
    return this.config ?? {
      workMins: 25,
      breakMins: 5,
      longBreakMins: 15,
      dailyGoal: 8
    };
  }
  async saveSettings(config) {
    this.config = config;
  }
};
export {
  DEFAULT_CONFIG,
  MemoryStorageAdapter,
  TimerEventEmitter,
  createTimerEmitter,
  createTimerStore
};
//# sourceMappingURL=index.js.map